import { DocumentRevisionStatus } from '@/shared/lib';
import type { RecordItem, RecordRevision } from '../model/types';

/**
 * Placeholder for the "Indexação" column. The indexer and its versions have no
 * backend source — no column in the schema and no field in any endpoint — so the
 * integration renders a dash instead of inventing a contract.
 */
export const RECORD_INDEXING_PLACEHOLDER = '—';

interface EligibleMatrixRevisionResponse {
  id: number;
  revision: number;
  effectiveDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  status: string;
}

export interface EligibleMatrixResponse {
  id: number;
  name: string | null;
  prefix: string;
  sequence: number | null;
  /**
   * Id of the revision that represents the record in the listing. Read from here rather than
   * from `revisions[0]`, because the sort below reorders the array.
   */
  representingRevisionId?: number;
  /** The newest revision's creation instant, which is what `audit.createdAt` shows. */
  createdAt?: string | null;
  revisions: EligibleMatrixRevisionResponse[];
}

/** Descending by revision number, which is the order AC31 asks the expansion to display. */
function sortRevisionsDescending(
  revisions: EligibleMatrixRevisionResponse[]
): EligibleMatrixRevisionResponse[] {
  return [...revisions].sort((left, right) => right.revision - left.revision);
}

function toRecordRevision(revision: EligibleMatrixRevisionResponse): RecordRevision {
  return {
    id: revision.id,
    revision: revision.revision,
    homologationDate: revision.effectiveDate ?? '',
    status: revision.status as DocumentRevisionStatus,
  };
}

export function toRecordItem(response: EligibleMatrixResponse): RecordItem {
  const revisions = sortRevisionsDescending(response.revisions);
  const representing =
    revisions.find((candidate) => candidate.id === response.representingRevisionId) ?? revisions[0];

  return {
    id: response.id,
    code: response.prefix,
    title: response.name ?? '',
    indexing: RECORD_INDEXING_PLACEHOLDER,
    indexingVersions: [],
    isActive: representing.status !== 'INACTIVE',
    generatedRecordsCount: 0,
    latestRevision: toRecordRevision(representing),
    // The listing returns every eligible revision of the record, so the expansion lists the
    // siblings of the representing one, excluding it because the main row already shows it.
    olderRevisions: revisions
      .filter((candidate) => candidate.id !== representing.id)
      .map(toRecordRevision),
    audit: {
      createdAt: response.createdAt ?? null,
      createdBy: null,
      updatedAt: representing.updatedAt,
      updatedBy: null,
    },
  };
}

/** A record with no eligible revision has no row to render, and no revision to link. */
export function hasEligibleRevision(response: EligibleMatrixResponse): boolean {
  return response.revisions.length > 0;
}
